import styleIcon from 'uiComponents/studio/icons/style.svg'
import copyIcon from 'uiComponents/studio/icons/copy.svg'
import restrictionsIcon from 'uiComponents/studio/icons/target.svg'
import settingsIcon from 'uiComponents/studio/icons/settings.svg'
import { SectionObject } from 'uiComponents/studio/menuItem'
import { t } from 'translations'
import { fontOptions } from '../shared'

export interface FormDefinitionSchema {
    text: SectionObject
    style: SectionObject
    targeting: SectionObject
    settings: SectionObject
}

export const getTriggerButtonFormDefinition = (): FormDefinitionSchema => ({
    text: {
        name: 'text',
        label: t('engageTools.studio.config.steps.text', { step: 1 }),
        header: t('engageTools.studio.config.headers.createMessage'),
        icon: copyIcon,
        description: t('engageTools.studio.triggerButton.descriptions.text'),
        sections: [
            {
                name: 'Languages',
                label: t('engageTools.studio.config.sections.languages'),
                fields: [],
            },
        ],
    },
    style: {
        name: 'style',
        label: t('engageTools.studio.config.steps.style', { step: 2 }),
        header: t('engageTools.studio.config.headers.designLookAndFeel'),
        icon: styleIcon,
        description: t('engageTools.studio.triggerButton.descriptions.style'),
        sections: [
            {
                name: t('engageTools.studio.config.sections.general'),
                fields: [
                    {
                        name: 'font-family',
                        type: 'select',
                        availableOptions: [...fontOptions],
                        label: t('engageTools.studio.config.fields.fontFamily'),
                        span: 8,
                        path: 'styling.fontFamily',
                        defaultValue: 'Roboto',
                    },
                    {
                        name: 'font-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.textSize'),
                        span: 8,
                        path: 'styling.tbFontSize',
                        defaultValue: 'medium',
                    },
                    {
                        name: 'font-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.fontColor'),
                        span: 6,
                        path: 'styling.fontColor',
                        defaultValue: '',
                    },
                    {
                        name: 'background-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.backgroundColor'),
                        span: 6,
                        path: 'styling.bgColor',
                        defaultValue: '',
                    },
                    {
                        name: 'background-opacity',
                        type: 'select',
                        label: t('engageTools.studio.config.fields.backgroundOpacity'),
                        span: 6,
                        path: 'styling.bgOpacity',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.opaque'),
                                value: '1',
                            },
                            {
                                name: t('engageTools.studio.config.options.transparent'),
                                value: '0.8',
                            },
                        ],
                        defaultValue: '1',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.icon'),
                fields: [
                    {
                        name: 'icon-source',
                        type: 'upload',
                        label: '',
                        span: 12,
                        path: 'styling.iconButton.iconSource',
                        defaultValue: '',
                    },
                    {
                        name: 'icon-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.svgIconColor'),
                        span: 6,
                        path: 'styling.iconButton.iconColor',
                        defaultValue: '',
                    },
                    {
                        name: 'background-color',
                        type: 'picker',
                        label: t('engageTools.studio.config.fields.backgroundColor'),
                        span: 6,
                        path: 'styling.iconButton.bgColor',
                        defaultValue: '',
                    },
                ],
            },
            {
                name: t('engageTools.studio.config.sections.minimizeIcon'),
                fields: [
                    {
                        name: 'minimize',
                        type: 'toggle',
                        label: t('engageTools.studio.config.fields.enableMinimizing'),
                        span: 6,
                        path: 'xButtonVisible',
                        defaultValue: true,
                        infotip: t('engageTools.studio.triggerButton.infotips.minimize'),
                    },
                    {
                        name: 'x-button-size',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.small'),
                                value: 'small',
                            },
                            {
                                name: t('engageTools.studio.config.options.medium'),
                                value: 'medium',
                            },
                            {
                                name: t('engageTools.studio.config.options.large'),
                                value: 'large',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.iconSize'),
                        span: 6,
                        path: 'styling.xButton.size',
                        defaultValue: 'small',
                    },
                    {
                        name: 'x-button-type',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.light'),
                                value: 'light',
                            },
                            {
                                name: t('engageTools.studio.config.options.dark'),
                                value: 'dark',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.iconType'),
                        span: 6,
                        path: 'styling.xButton.type',
                        defaultValue: 'light',
                    },
                ],
            },
        ],
    },
    targeting: {
        name: 'targeting',
        label: t('engageTools.studio.config.steps.targeting', { step: 3 }),
        header: t('engageTools.studio.config.headers.setRestrictions'),
        icon: restrictionsIcon,
        description: t('engageTools.studio.triggerButton.descriptions.targeting'),
        sections: [
            {
                name: 'Audience',
                fields: [],
            },
            {
                name: t('engageTools.studio.config.sections.displayOn'),
                infotip: t('engageTools.studio.triggerButton.infotips.displayOn'),
                fields: [
                    {
                        name: 'display',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.allDevices'),
                                value: 'all',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyMobile'),
                                value: 'mobile',
                            },
                            {
                                name: t('engageTools.studio.config.options.onlyDesktop'),
                                value: 'desktop',
                            },
                        ],
                        label: '',
                        span: 12,
                        path: 'target',
                        defaultValue: 'all',
                    },
                ],
            },
            {
                name: 'Blacklist',
                label: t('engageTools.studio.triggerButton.blacklistLabel'),
                infotip: t('engageTools.studio.triggerButton.infotips.blacklist'),
                fields: [],
            },
        ],
    },
    settings: {
        name: 'settings',
        label: t('engageTools.studio.config.steps.settings', { step: 4 }),
        header: t('engageTools.studio.config.headers.setVisibilityRules'),
        icon: settingsIcon,
        description: t('engageTools.studio.triggerButton.descriptions.settings'),
        sections: [
            {
                name: 'Display rules',
                label: t('engageTools.studio.config.sections.displayRules'),
                fields: [
                    {
                        name: 'display-trigger',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.uponPageLoad'),
                                value: 'onLoad',
                            },
                            {
                                name: t('engageTools.studio.config.options.withDelay'),
                                value: 'afterDelay',
                            },
                            {
                                name: t('engageTools.studio.config.options.onExitIntent'),
                                value: 'exitIntent',
                            },
                            {
                                name: t('engageTools.studio.config.options.onScrollDown50'),
                                value: 'scrollDown50',
                            },
                            {
                                name: t('engageTools.studio.config.options.onScrollUp'),
                                value: 'scrollUp',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.display'),
                        span: 8,
                        path: 'trigger',
                        defaultValue: 'afterDelay',
                    },
                    {
                        name: 'show-after',
                        type: 'number',
                        min: 0,
                        max: 120,
                        unit: 'sec',
                        label: t('engageTools.studio.config.fields.showAfter'),
                        span: 8,
                        path: 'displayDelayPeriod',
                        defaultValue: 5,
                    },
                    {
                        name: 'position',
                        type: 'select',
                        availableOptions: [
                            {
                                name: t('engageTools.studio.config.options.bottomLeft'),
                                value: 'bottom-left',
                            },
                            {
                                name: t('engageTools.studio.config.options.bottomRight'),
                                value: 'bottom-right',
                            },
                            {
                                name: t('engageTools.studio.config.options.topLeft'),
                                value: 'top-left',
                            },
                            {
                                name: t('engageTools.studio.config.options.topRight'),
                                value: 'top-right',
                            },
                        ],
                        label: t('engageTools.studio.config.fields.position'),
                        span: 8,
                        path: 'tbPosition',
                        defaultValue: 'bottom-left',
                    },
                ],
            },
        ],
    },
})
